/**
 * Per-manager fitting — turn a league's own auction history into a strategy
 * profile for each real manager.
 *
 * Pure functions. No UI, no engine, no I/O. Nothing here is wired into pricing
 * or bidding; a caller decides what, if anything, to do with the output.
 *
 * Input is the auctionHistory captured at import (see sleeperImport.js) plus
 * the league members list. Picks are matched to managers by pickedBy →
 * members[].userId, which means histories imported before those fields existed
 * fit nothing and every manager comes back fitted:false.
 */

import { ARCHETYPES, MODIFIERS } from "../data/archetypes";

// ── Name validation ─────────────────────────────────────────────────────────
// Every archetype/modifier this module can emit is checked against the real
// tables at module load. If a name here ever drifts from data/archetypes.js,
// the fit degrades to the safe default instead of writing a config the engine
// will silently read as {} — that failure mode is the all-Balanced bug, and it
// is much easier to debug as a console warning than as flat draft results.
const FALLBACK_ARCHETYPE = "Balanced";

const WANTED_ARCHETYPES = ["Stars & Scrubs", "Balanced", "RB Heavy", "WR Heavy"];
const WANTED_MODIFIERS = ["Punt QB", "Elite QB", "Punt TE", "Elite TE", "Patient Spender"];

const VALID_ARCHETYPES = new Set(WANTED_ARCHETYPES.filter(n => !!ARCHETYPES[n]));
const VALID_MODIFIERS = new Set(WANTED_MODIFIERS.filter(n => !!MODIFIERS[n]));

if (typeof console !== "undefined") {
  const missingA = WANTED_ARCHETYPES.filter(n => !ARCHETYPES[n]);
  const missingM = WANTED_MODIFIERS.filter(n => !MODIFIERS[n]);
  if (missingA.length) console.warn(`[leagueMemberProfiles] unknown archetypes ignored: ${missingA.join(", ")}`);
  if (missingM.length) console.warn(`[leagueMemberProfiles] unknown modifiers ignored: ${missingM.join(", ")}`);
}
if (!ARCHETYPES[FALLBACK_ARCHETYPE]) {
  throw new Error("[leagueMemberProfiles] fallback archetype 'Balanced' is missing from ARCHETYPES");
}

const archetypeOr = name => (VALID_ARCHETYPES.has(name) ? name : FALLBACK_ARCHETYPE);
const modifierOr = name => (VALID_MODIFIERS.has(name) ? name : null);

// ── Thresholds ──────────────────────────────────────────────────────────────
const MIN_PICKS = 25;          // below this a manager's shares are noise
const MIN_SEASONS = 2;         // one draft is a mood, not a tendency

// Concentration is measured PER SEASON and averaged. Pooling seasons first
// divides three drafts' worth of spend by a single top-3, which lands every
// manager far below any sensible threshold and makes Stars & Scrubs
// unreachable — measured 0.236-0.349 across a real 12-team league.
//
// TOP3_REF is the normal top-3 share for a 12-team auction: between the
// measured MFL 12-team figure (56%) and Smithtown's (61%). The band is
// deliberately wide — inside it the rule says nothing and lets the position
// read or the default decide, rather than forcing every manager into a verdict
// the data doesn't support.
const TOP3_REF = 0.58;
const TOP3_BAND = 0.06;
const TOP3_STARS = TOP3_REF + TOP3_BAND;      // >= 0.64
const TOP3_BALANCED = TOP3_REF - TOP3_BAND;   // <= 0.52

const POS_HEAVY_EDGE = 0.08;   // points above the league's own share
const QB_PUNT = 0.04, QB_ELITE = 0.12;
const TE_PUNT = 0.04, TE_ELITE = 0.12;

// Patient Spender needs BOTH gates. Rank alone is purely relative — the bottom
// third of any league always exists, so a room of twelve aggressive spenders
// would still produce four "patient" ones. The magnitude gate is what makes
// the label describe behavior instead of standings.
const PATIENT_PERCENTILE = 1 / 3;  // bottom third of early spend
const PATIENT_MIN_GAP = 0.08;      // and at least this far below league mean
const EARLY_WINDOW = 0.25;     // first quarter of the auction
const BIG_BUY = 20;            // $20+ is where overpay is measurable
// Ratio ceiling → overpay value; anything at or above the last ceiling gets
// OVERPAY_MAX. Matches the 0-4 scale the archetypes themselves use.
const OVERPAY_BANDS = [
  [0.95, 0], [1.05, 1], [1.12, 2], [1.20, 3],
];
const OVERPAY_MAX = 4;

const sum = (arr, f) => arr.reduce((t, x) => t + f(x), 0);

/**
 * @param {Array} auctionHistory — [{season, draftId, picks:[…]}]
 * @param {Array} members — [{userId, displayName, teamName}]
 * @returns {Array} one row per member, in the order members were given
 */
export function fitMemberProfiles(auctionHistory, members) {
  const roster = Array.isArray(members) ? members : [];
  if (!roster.length) return [];

  // Blank rows for every member up front, so a league with no usable history
  // still returns a complete, well-shaped table.
  const blank = m => ({
    userId: m?.userId ?? null,
    displayName: m?.displayName ?? "",
    archetype: FALLBACK_ARCHETYPE,
    modifiers: [],
    overpay: null,          // null = use the archetype's own overpay
    nPicks: 0,
    nSeasons: 0,
    fitted: false,
  });
  if (!Array.isArray(auctionHistory) || !auctionHistory.length) return roster.map(blank);

  // ── Per-season, per-manager collection ───────────────────────────────────
  // byUser: userId -> { picks[], seasons:Set, earlyShares[], top3Shares[] }
  // The per-season arrays are averaged later; anything measured within a draft
  // has to be collected within the draft.
  const byUser = new Map();
  const leaguePicks = [];

  for (const season of auctionHistory) {
    // The auction is the non-keeper sequence. Keepers occupy the low pick
    // numbers in Sleeper but aren't auction events, so including them would
    // put a manager's "early" window inside picks nobody bid on.
    // Array order is draft order — verified against pick_no on the source.
    const live = (season?.picks || []).filter(
      p => !p.keeper && Number.isFinite(p.amount) && p.amount > 0
    );
    if (!live.length) continue;
    leaguePicks.push(...live);

    const cutoff = Math.max(1, Math.round(live.length * EARLY_WINDOW));
    const seasonPicks = new Map();   // userId -> picks[] for THIS season
    const seasonEarly = new Map();   // userId -> early spend for THIS season
    live.forEach((p, i) => {
      if (!p.pickedBy) return;
      if (!seasonPicks.has(p.pickedBy)) seasonPicks.set(p.pickedBy, []);
      seasonPicks.get(p.pickedBy).push(p);
      if (i < cutoff) seasonEarly.set(p.pickedBy, (seasonEarly.get(p.pickedBy) || 0) + p.amount);

      const u = byUser.get(p.pickedBy) || { picks: [], seasons: new Set(), earlyShares: [], top3Shares: [] };
      u.picks.push(p);
      u.seasons.add(season.season);
      byUser.set(p.pickedBy, u);
    });
    // One value per manager per season, averaged later, so a season where
    // someone spent little doesn't outweigh a full one.
    for (const [userId, ps] of seasonPicks) {
      const total = sum(ps, p => p.amount);
      if (total <= 0) continue;
      const u = byUser.get(userId);
      u.earlyShares.push((seasonEarly.get(userId) || 0) / total);
      const top3 = [...ps].sort((a, b) => b.amount - a.amount).slice(0, 3);
      u.top3Shares.push(sum(top3, p => p.amount) / total);
    }
  }

  // ── League baselines ─────────────────────────────────────────────────────
  const leagueTotal = sum(leaguePicks, p => p.amount);
  if (!leagueTotal) return roster.map(blank);

  const leagueShare = pos =>
    sum(leaguePicks.filter(p => p.pos === pos), p => p.amount) / leagueTotal;
  const leagueRB = leagueShare("RB");
  const leagueWR = leagueShare("WR");

  const leagueBig = leaguePicks.filter(p => p.amount >= BIG_BUY);
  const leagueBigAvg = leagueBig.length ? sum(leagueBig, p => p.amount) / leagueBig.length : 0;

  // ── Per-manager statistics ───────────────────────────────────────────────
  const stats = new Map();
  for (const [userId, u] of byUser) {
    const total = sum(u.picks, p => p.amount);
    if (!total) continue;
    const share = pos => sum(u.picks.filter(p => p.pos === pos), p => p.amount) / total;
    const big = u.picks.filter(p => p.amount >= BIG_BUY);
    const mean = arr => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null);
    stats.set(userId, {
      nPicks: u.picks.length,
      nSeasons: u.seasons.size,
      // Per-season concentration, averaged — see TOP3_REF.
      top3Share: mean(u.top3Shares),
      rbShare: share("RB"),
      wrShare: share("WR"),
      qbShare: share("QB"),
      teShare: share("TE"),
      earlySpendShare: mean(u.earlyShares),
      bigAvg: big.length ? sum(big, p => p.amount) / big.length : null,
    });
  }

  const isFitted = s => s && s.nPicks >= MIN_PICKS && s.nSeasons >= MIN_SEASONS;

  // Patient Spender is relative, so the threshold comes from the fitted
  // managers only — unfitted ones get no modifiers and would just drag the
  // percentile around.
  const earlyValues = [...stats.entries()]
    .filter(([, s]) => isFitted(s) && s.earlySpendShare != null)
    .map(([, s]) => s.earlySpendShare)
    .sort((a, b) => a - b);
  const patientCutoff = earlyValues.length
    ? earlyValues[Math.max(0, Math.ceil(earlyValues.length * PATIENT_PERCENTILE) - 1)]
    : null;
  // Mean of the same per-manager statistic the rank gate ranks, so the two
  // gates are measured on one scale.
  const leagueMeanEarly = earlyValues.length
    ? earlyValues.reduce((a, b) => a + b, 0) / earlyValues.length
    : null;

  // ── Assemble ─────────────────────────────────────────────────────────────
  return roster.map(m => {
    const s = stats.get(m?.userId);
    if (!isFitted(s)) {
      const row = blank(m);
      // Report what was actually seen even when it wasn't enough to fit —
      // that's what makes a fitted:false row diagnosable.
      if (s) { row.nPicks = s.nPicks; row.nSeasons = s.nSeasons; }
      return row;
    }

    // Archetype. A position-heavy read beats the concentration read: it says
    // something about WHERE the money went, which is the stronger signal.
    let archetype = null;
    const rbEdge = s.rbShare - leagueRB;
    const wrEdge = s.wrShare - leagueWR;
    if (rbEdge >= POS_HEAVY_EDGE || wrEdge >= POS_HEAVY_EDGE) {
      archetype = rbEdge >= wrEdge ? "RB Heavy" : "WR Heavy";
    } else if (s.top3Share >= TOP3_STARS) {
      archetype = "Stars & Scrubs";
    } else if (s.top3Share <= TOP3_BALANCED) {
      archetype = "Balanced";
    }

    const modifiers = [];
    const push = name => { const v = modifierOr(name); if (v && !modifiers.includes(v)) modifiers.push(v); };
    if (s.qbShare < QB_PUNT) push("Punt QB");
    else if (s.qbShare > QB_ELITE) push("Elite QB");
    if (s.teShare < TE_PUNT) push("Punt TE");
    else if (s.teShare > TE_ELITE) push("Elite TE");
    // Rank finds the candidates; deviation confirms the behavior. Both gates,
    // or a league of uniformly aggressive spenders mints Patient Spenders out
    // of whoever happened to place last.
    if (
      s.earlySpendShare != null &&
      patientCutoff != null && s.earlySpendShare <= patientCutoff &&
      leagueMeanEarly != null && s.earlySpendShare <= leagueMeanEarly - PATIENT_MIN_GAP
    ) {
      push("Patient Spender");
    }

    // Overpay. Undefined without $20+ buys to compare, and null there means
    // "use the archetype default" — the same sentinel team configs already use.
    let overpay = null;
    if (s.bigAvg != null && leagueBigAvg > 0) {
      const ratio = s.bigAvg / leagueBigAvg;
      overpay = OVERPAY_MAX;
      for (const [limit, value] of OVERPAY_BANDS) {
        if (ratio < limit) { overpay = value; break; }
      }
    }

    return {
      userId: m.userId ?? null,
      displayName: m.displayName ?? "",
      archetype: archetypeOr(archetype),
      modifiers,
      overpay,
      nPicks: s.nPicks,
      nSeasons: s.nSeasons,
      fitted: true,
    };
  });
}
